/**
 * Mirrors the API's plan-recommendation response; restated because the apps share no
 * workspace. Vocabulary the API owns is typed as `string` so an unrecognized value
 * degrades to a missing badge rather than a parse failure.
 */

export const RECOMMENDATION_REASON_CODES = [
  'caller_recommended',
  'only_eligible_offer',
  'best_tier_for_practice',
] as const

export type RecommendationReasonCode =
  (typeof RECOMMENDATION_REASON_CODES)[number]

export type Rationale = {
  code: RecommendationReasonCode
  params: Record<string, string | number>
}

export type TierPrice = {
  interval: string
  productId: string
  defaultPriceId: string
  unitAmountInCents: number | null
}

export type RankedTierOffer = {
  tier: string
  displayName: string
  featureIds: string[]
  prices: TierPrice[]
  rank: number
  isRecommended: boolean
  rationale: Rationale | null
}

export type RecommendationSignalName = 'taxEntityType' | 'practiceType'

export type RecommendationResult =
  | {
      status: 'ok'
      offers: RankedTierOffer[]
      recommendedTier: string | null
      strategy: string
    }
  | { status: 'insufficient_signals'; missing: RecommendationSignalName[] }

/** Web-only: the fetch layer turns the flag-off 404 into a state the screen can render. */
export type RecommendedOffersFetchResult =
  | RecommendationResult
  | { status: 'feature_disabled' }

export const BILLING_INTERVALS = { month: 'month', year: 'year' } as const

export type BillingInterval =
  (typeof BILLING_INTERVALS)[keyof typeof BILLING_INTERVALS]

export const priceForInterval = (
  offer: RankedTierOffer,
  interval: BillingInterval
): TierPrice | undefined =>
  offer.prices.find((price) => price.interval === interval)
